import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import Modal from '../Modal';
import {
  StatusDto,
  StatusGroup,
  StatusType,
  UpdateStatusDto,
  CreateStatusDto,
  STATUS_COLORS
} from '../../services/statusService';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);

  span {
    color: var(--error-color);
  }
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 14px;
  transition: all 0.2s ease;
  direction: rtl;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(0, 142, 162, 0.1);
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
`;

const TextArea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 14px;
  transition: all 0.2s ease;
  resize: vertical;
  min-height: 80px;
  direction: rtl;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(0, 142, 162, 0.1);
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 14px;
  transition: all 0.2s ease;
  background: white;
  cursor: pointer;
  direction: rtl;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(0, 142, 162, 0.1);
  }
`;

const ColorPicker = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ColorOption = styled.button<{ color: string; selected: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: var(--border-radius-sm);
  background: ${props => props.color};
  border: 2px solid ${props => props.selected ? 'var(--text-primary)' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    transform: scale(1.1);
  }

  ${props => props.selected && `
    &:after {
      content: '✓';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: white;
      font-weight: bold;
    }
  `}
`;

const CustomColorInput = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: var(--text-primary);

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-color);
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--border-color);
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background: var(--primary-color);
    color: white;
    border: 1px solid var(--primary-color);
    &:hover { background: transparent; color: var(--primary-color); }
  ` : `
    background: var(--surface-color);
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    &:hover { background: var(--hover-background); }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface StatusEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (status: CreateStatusDto | UpdateStatusDto) => Promise<void>;
  status: StatusDto | null;
  group: StatusGroup;
  existingStatuses: StatusDto[];
}

const StatusEditModal: React.FC<StatusEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  status,
  group,
  existingStatuses
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: STATUS_COLORS[0],
    statusType: StatusType.Standard,
    isDefault: false,
    countInPipeline: true,
    countAsConversion: false,
    createReminder: false,
    isActive: true,
    expectedDurationDays: undefined as number | undefined,
    maxDurationDays: undefined as number | undefined,
    isHidden: false,
    autoAlertDays: undefined as number | undefined,
    alertContent: ''
  });

  const [saving, setSaving] = useState(false);
  const [useCustomColor, setUseCustomColor] = useState(false);

  useEffect(() => {
    if (status) {
      setFormData({
        name: status.name,
        description: status.description || '',
        color: status.color,
        statusType: status.statusType,
        isDefault: status.isDefault,
        countInPipeline: status.countInPipeline,
        countAsConversion: status.countAsConversion,
        createReminder: status.createReminder,
        isActive: status.isActive,
        expectedDurationDays: status.expectedDurationDays,
        maxDurationDays: status.maxDurationDays,
        isHidden: status.isHidden,
        autoAlertDays: status.autoAlertDays,
        alertContent: status.alertContent || ''
      });
      setUseCustomColor(!STATUS_COLORS.includes(status.color));
    } else {
      // Reset form for new status
      setFormData({
        name: '',
        description: '',
        color: STATUS_COLORS[0],
        statusType: StatusType.Standard,
        isDefault: false,
        countInPipeline: true,
        countAsConversion: false,
        createReminder: false,
        isActive: true,
        expectedDurationDays: undefined,
        maxDurationDays: undefined,
        isHidden: false,
        autoAlertDays: undefined,
        alertContent: ''
      });
      setUseCustomColor(false);
    }
  }, [status]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('נא להזין שם סטטוס');
      return;
    }

    setSaving(true);
    try {
      const dataToSave: any = {
        ...formData,
        group,
        sortOrder: status?.sortOrder || existingStatuses.length,
        nextStatuses: status?.nextStatuses || []
      };

      await onSave(dataToSave);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={status ? 'עריכת סטטוס' : 'יצירת סטטוס חדש'}
      width="600px"
    >
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>
            שם הסטטוס <span>*</span>
          </Label>
          <Input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="הזן שם סטטוס"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>תיאור</Label>
          <TextArea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="הזן תיאור לסטטוס (אופציונלי)"
          />
        </FormGroup>

        <FormRow>
          <FormGroup>
            <Label>סוג סטטוס</Label>
            <Select
              value={formData.statusType}
              onChange={(e) => setFormData({ ...formData, statusType: Number(e.target.value) })}
            >
              <option value={StatusType.Standard}>רגיל</option>
              <option value={StatusType.Initial}>התחלתי</option>
              <option value={StatusType.InProgress}>בתהליך</option>
              <option value={StatusType.Success}>הצלחה</option>
              <option value={StatusType.Cancelled}>בוטל</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>צבע</Label>
            <ColorPicker>
              {STATUS_COLORS.map((color) => (
                <ColorOption
                  key={color}
                  type="button"
                  color={color}
                  selected={formData.color === color && !useCustomColor}
                  onClick={() => {
                    setFormData({ ...formData, color });
                    setUseCustomColor(false);
                  }}
                />
              ))}
              <CustomColorInput>
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => {
                    setFormData({ ...formData, color: e.target.value });
                    setUseCustomColor(true);
                  }}
                />
              </CustomColorInput>
            </ColorPicker>
          </FormGroup>
        </FormRow>

        <SectionTitle>הגדרות תצוגה</SectionTitle>

        <FormGroup>
          <Checkbox>
            <input
              type="checkbox"
              checked={formData.isDefault}
              onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
            />
            סטטוס ברירת מחדל
          </Checkbox>

          <Checkbox>
            <input
              type="checkbox"
              checked={formData.countInPipeline}
              onChange={(e) => setFormData({ ...formData, countInPipeline: e.target.checked })}
            />
            כלול בחישובי Pipeline
          </Checkbox>

          <Checkbox>
            <input
              type="checkbox"
              checked={formData.countAsConversion}
              onChange={(e) => setFormData({ ...formData, countAsConversion: e.target.checked })}
            />
            חשב כהמרה מוצלחת
          </Checkbox>

          <Checkbox>
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
            />
            סטטוס פעיל
          </Checkbox>

          <Checkbox>
            <input
              type="checkbox"
              checked={formData.isHidden}
              onChange={(e) => setFormData({ ...formData, isHidden: e.target.checked })}
            />
            הסתר מהממשק
          </Checkbox>
        </FormGroup>

        <SectionTitle>הגדרות זמן והתראות</SectionTitle>

        <FormRow>
          <FormGroup>
            <Label>משך זמן צפוי (ימים)</Label>
            <Input
              type="number"
              value={formData.expectedDurationDays || ''}
              onChange={(e) => setFormData({
                ...formData,
                expectedDurationDays: e.target.value ? Number(e.target.value) : undefined
              })}
              min="1"
              placeholder="0"
            />
          </FormGroup>

          <FormGroup>
            <Label>זמן מקסימלי (ימים)</Label>
            <Input
              type="number"
              value={formData.maxDurationDays || ''}
              onChange={(e) => setFormData({
                ...formData,
                maxDurationDays: e.target.value ? Number(e.target.value) : undefined
              })}
              min="1"
              placeholder="0"
            />
          </FormGroup>
        </FormRow>

        <FormGroup>
          <Checkbox>
            <input
              type="checkbox"
              checked={formData.createReminder}
              onChange={(e) => setFormData({ ...formData, createReminder: e.target.checked })}
            />
            יצירת תזכורת אוטומטית
          </Checkbox>
        </FormGroup>

        <FormRow>
          <FormGroup>
            <Label>התראה אחרי (ימים)</Label>
            <Input
              type="number"
              value={formData.autoAlertDays || ''}
              onChange={(e) => setFormData({
                ...formData,
                autoAlertDays: e.target.value ? Number(e.target.value) : undefined
              })}
              min="1"
              placeholder="0"
            />
          </FormGroup>

          <FormGroup>
            <Label>תוכן ההתראה</Label>
            <Input
              type="text"
              value={formData.alertContent}
              onChange={(e) => setFormData({ ...formData, alertContent: e.target.value })}
              placeholder="הזן תוכן התראה"
            />
          </FormGroup>
        </FormRow>

        <ButtonGroup>
          <Button type="button" onClick={onClose}>
            ביטול
          </Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? 'שומר...' : (status ? 'עדכן סטטוס' : 'צור סטטוס')}
          </Button>
        </ButtonGroup>
      </Form>
    </Modal>
  );
};

export default StatusEditModal;